import * as React from 'react'

export interface MaterialSymbolProps {
  /** Material Symbols Rounded ligature name, e.g. "local_florist" */
  name: string
  /** font-size in px (controls the rendered glyph size) */
  size?: number
  /** css color applied to the glyph */
  color?: string
  /** filled vs outlined variant (maps to the 'FILL' axis) */
  fill?: boolean
  /** optical weight (maps to the 'wght' axis), defaults to 400 */
  weight?: number
  /** Accessible label. Icons are decorative (aria-hidden) by default —
   *  pass a label ONLY when the glyph is the sole content of a control
   *  that has no other accessible name. */
  'aria-label'?: string
  className?: string
  style?: React.CSSProperties
}

/**
 * Renders a Material Symbols Rounded glyph via font ligatures.
 *
 * The `.msr` global class (see GlobalStyle.ts) supplies the font-family and
 * base rendering; this component layers per-instance size/color/axis settings.
 */
const MaterialSymbol = ({
  name,
  size,
  color,
  fill,
  weight,
  'aria-label': ariaLabel,
  className,
  style,
}: MaterialSymbolProps): JSX.Element => (
  <span
    className={`msr ${className || ''}`}
    // The ligature NAME is literal text ("barcode_scanner") — hide it from
    // assistive tech unless an explicit label is provided; otherwise every
    // icon+label control reads its glyph word aloud.
    aria-hidden={ariaLabel ? undefined : true}
    aria-label={ariaLabel}
    role={ariaLabel ? 'img' : undefined}
    style={{
      fontSize: size,
      color,
      fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' ${weight || 400}`,
      ...style,
    }}
  >
    {name}
  </span>
)

export default MaterialSymbol
